"use client";
import styles from "./ProductResults.module.scss";

import { useRef, useState } from "react";

import { EnergyType } from "@/types/data";
import { track } from "@/utilities/analytics";
import { scrollToResultsIfRequested } from "@/utilities/results-scroll";
import { getPlans } from "./ProductResultsActions";
import { useLookupStore } from "@/stores/lookupStore";
import clsx from "clsx";
import { isEmpty } from "lodash";
import { MaterialIcon } from "../MaterialIcon/MaterialIcon";
import { MultipleNMIAddressSelect } from "./MultipleNMIAddressSelect/MultipleNMIAddressSelect";
import { ErrorCard } from "./Product/ErrorCard/ErrorCard";
import { CheckGasAvailableCard } from "./Product/GasAvailableCard/GasAvailableCard";
import { ProductCard } from "./Product/ProductCard/ProductCard";

import { useEffect } from "react";
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";

import { useOnboardingStore } from "@/stores/onboardingStore";
import type { MultipleNmiAddressSelectContent, NmiSearchModalContent } from "@/sanity/queries";

interface ProductResultsProps {
	addressKey?: string;
	nmi?: string;
	promo?: string;
	energy: EnergyType;
	address?: string;
	nmiSearchModalContent?: NmiSearchModalContent | null;
	multipleNmiAddressSelectContent?: MultipleNmiAddressSelectContent | null;
}

const isPromoApplied = (planPromoCode: string | undefined, promo: string | undefined) =>
	Boolean(
		promo &&
		planPromoCode &&
		planPromoCode !== "Invalid Promo Code" &&
		planPromoCode !== "N" &&
		planPromoCode.toUpperCase() === promo.toUpperCase(),
	);

type LookupData = {
	success?: boolean;
	status?: string;
	isCommercial?: boolean;
	nmi?: string;
	plan?: { promoCode?: string; [key: string]: any };
};

/**
 * Identifies one lookup outcome, so the same result is not reported twice
 * across re-renders.
 */
const outcomeSignature = (data: LookupData) =>
	[data?.status, data?.success, data?.nmi, data?.plan?.PROD1].join("|");

/**
 * Report what the customer was actually shown.
 *
 * Driven off the resolved data rather than the fetch handler, because
 * MultipleNMIAddressSelect sets results directly and would otherwise bypass
 * this entirely.
 */
const trackLookupOutcome = (
	data: LookupData,
	energy: EnergyType,
	promo?: string,
	gasAvailable?: boolean | null,
) => {
	// Nothing has been shown yet.
	if (!data || data.status === "loading") {
		return;
	}

	// The address matched several NMIs, so there is no outcome yet — the
	// customer picks one and a fresh lookup runs.
	if (data.status === "multipleNMI") {
		return;
	}

	if (!data.success) {
		track({ event: "plans_unavailable", energy, reason: "lookup_failed" });
		return;
	}

	// PROD1 is the plan name and gates whether a plan was returned at all —
	// the same check the render uses to decide between a card and an error.
	const product: string | undefined = data?.plan?.PROD1;

	if (isEmpty(product)) {
		track({ event: "plans_unavailable", energy, reason: "no_plan" });
		return;
	}

	// A gas plan is only ever shown once the pipeline locator confirms a
	// connection at the address. Without this the customer is looking at a map,
	// not a plan.
	if (energy === "gas" && gasAvailable === false) {
		track({ event: "plans_unavailable", energy, reason: "gas_unavailable" });
		return;
	}

	track({
		event: "plan_viewed",
		energy,
		productCode: data?.plan?.PROD1_TEMPLATE ?? "",
		product: product as string,
		isCommercial: Boolean(data.isCommercial),
		promo: promo || undefined,
		promo_valid: promo ? isPromoApplied(data?.plan?.PROMOCODE, promo) : undefined,
	});
};

export const ProductResults = ({
	addressKey,
	nmi,
	promo,
	energy,
	address,
	nmiSearchModalContent,
	multipleNmiAddressSelectContent,
}: ProductResultsProps) => {
	const [data, setData] = useState<{
		isCommercial: boolean;
		signupUrl: string;
		tariffs?: string[];
		plan?: {
			promoCode?: string;
			[key: string]: any;
		};
		success?: boolean;
		status?: string;
		addresses?: { address: string; nmi: string }[]; // if multiple NMIs it will return this
		address?: { [key: string]: any };
		nmi?: string;
		promoCode?: string;
		aseXML?: string;
		installationTypeCode?: string;
	}>({
		signupUrl: "",
		tariffs: [],
		plan: {},
		success: true,
		status: "loading",
		addresses: [],
		isCommercial: false,
		nmi: "",
		promoCode: "",
	});

	const setOnboardingData = useOnboardingStore(state => state.setOnboardingData);

	// null until the pipeline locator reports back. Gas only.
	const [gasAvailable, setGasAvailable] = useState<boolean | null>(null);

	const trackedOutcome = useRef<string | null>(null);

	// Arriving from another page: anchor to the results straight away so the
	// loading state is what's on screen while the plans load.
	useEffect(() => {
		requestAnimationFrame(scrollToResultsIfRequested);
	}, []);

	useEffect(() => {
		const lookupEnergy: EnergyType = nmi ? "electricity" : energy;

		const signature = `${outcomeSignature(data)}|${gasAvailable}`;

		if (trackedOutcome.current === signature) {
			return;
		}

		trackedOutcome.current = signature;

		// A gas plan exists, but the customer is shown the pipeline locator
		// instead of a card until they check their address.
		if (
			lookupEnergy === "gas" &&
			data?.success &&
			data?.status === "success" &&
			gasAvailable === null
		) {
			track({ event: "gas_availability_check", energy: "gas" });
			return;
		}

		trackLookupOutcome(data, lookupEnergy, promo, gasAvailable);
	}, [data, energy, nmi, promo, gasAvailable]);

	useEffect(() => {
		if (!addressKey && !nmi) return;
		const controller = new AbortController();
		const submittedLookup = useLookupStore.getState().lookup;
		const isCurrent = () =>
			!controller.signal.aborted && useLookupStore.getState().lookup === submittedLookup;
		const input = nmi
			? { nmi, energy: "electricity" as const, promo }
			: { addressKey: addressKey!, energy, promo };
		void getPlans(input, controller.signal)
			.then(result => {
				if (!isCurrent()) return;
				if (result.success && result.status === "success") {
					setOnboardingData({
						energy,
						isCommercial: result.isCommercial,
						plan: result.plan || {},
						nmi: result.nmi || "",
						promoCode: result.promoCode || "",
						address: result.address || {},
						aseXML: result.aseXML,
						installationTypeCode: result.installationTypeCode,
					});
				}
				setData(result);
				// Wait for the new plans to render before anchoring to them.
				requestAnimationFrame(scrollToResultsIfRequested);
			})
			.catch(() => {
				if (!isCurrent()) return;
				console.error("Failed to get plans");
				setData(current => ({ ...current, success: false, status: "error" }));
			});
		return () => controller.abort();
	}, [addressKey, nmi, promo, energy, setOnboardingData]);

	const isPromoValid = isPromoApplied(data?.plan?.PROMOCODE, promo);

	const validPlan = !isEmpty(data?.plan?.PROD1);

	if (data && data.status === "loading") {
		return (
			<div className={styles["loading"]}>
				<LoadingSpinner size="xl" />
				<p>Loading relevant products...</p>
			</div>
		);
	}

	return (
		<div>
			<div>
				{data.success && data.status == "success" && (
					<div className={styles["product-results"]}>
						{data?.plan?.PROMOCODE && data?.plan?.PROMOCODE !== "N" && isPromoValid && (
							<p
								className={clsx(
									styles["product-results__message"],
									styles["product-results__message--success"],
								)}
							>
								<MaterialIcon
									name="check_circle"
									className={styles["product-results__message-icon"]}
								/>
								Promo code {data?.plan?.PROMOCODE} has been applied
							</p>
						)}
						{data?.plan?.PROMOCODE && data?.plan?.PROMOCODE !== "N" && !isPromoValid && (
							<p
								className={clsx(
									styles["product-results__message"],
									styles["product-results__message--warn"],
								)}
							>
								<MaterialIcon
									name="warning"
									className={styles["product-results__message-icon"]}
								/>
								{data?.plan?.PROMOCODE}
							</p>
						)}
						{validPlan && data.plan ? (
							energy !== "gas" ? (
								<ProductCard
									energy={"electricity"}
									isCommercial={data.isCommercial}
									plan={data.plan}
									signupUrl={data.signupUrl}
									nmi={data.nmi}
									promoCode={data.promoCode}
								/>
							) : (
								<CheckGasAvailableCard
									address={address ?? ""}
									onAvailabilityChange={setGasAvailable}
								>
									<ProductCard
										energy={"gas"}
										isCommercial={data.isCommercial}
										plan={data.plan}
										signupUrl={data.signupUrl}
									/>
								</CheckGasAvailableCard>
							)
						) : (
							<ErrorCard error={{ message: "noPlan", name: "noPlan" }} />
						)}
					</div>
				)}
			</div>

			{data && data.status === "multipleNMI" && data.addresses && (
				<div className={styles["product-results"]}>
					<MultipleNMIAddressSelect
						nmis={data.addresses}
						promo={promo}
						nmiSearchModalContent={nmiSearchModalContent}
						content={multipleNmiAddressSelectContent}
					/>
				</div>
			)}

			{data && !data.success && (
				<div className={styles["product-results"]}>
					<ErrorCard error={{ message: "noPlan", name: "noPlan" }} />
				</div>
			)}
		</div>
	);
};
